/**
 * The route table — Spec 020 D5, the «с нуля» screens of Spec 022. Its own module so a test can check that every
 * link the bot sends still opens the right screen (tests/routes.test.mjs).
 */

import { Navigate, useParams, useSearchParams, type RouteObject } from "react-router-dom";

import { AgentScreenRoute } from "@/screens/AgentScreen";
import { AgentsScreen } from "@/screens/AgentsScreen";
import { CaseScreenRoute } from "@/screens/CaseScreen";
import { CasesScreen } from "@/screens/CasesScreen";
import { ConnectionScreen, ConnectionsScreen } from "@/screens/ConnectionsScreen";
import { DecideScreen } from "@/screens/DecideScreen";
import { DepartmentScreenRoute } from "@/screens/DepartmentScreen";
import { DepartmentsScreen } from "@/screens/DepartmentsScreen";
import { ExperienceScreen } from "@/screens/ExperienceScreen";
import { MethodDutyScreen } from "@/screens/MethodDutyScreen";
import { IdeaScreenRoute } from "@/screens/IdeaScreen";
import { IdeasScreen } from "@/screens/IdeasScreen";
import { NotificationsScreen } from "@/screens/NotificationsScreen";
import { OrderScreenRoute } from "@/screens/OrderScreen";
import { ClientScreenRoute } from "@/screens/OrdersScreen";
import { StatsScreen } from "@/screens/StatsScreen";
import { KnowledgeScreen } from "@/screens/KnowledgeScreen";
import { ProjectScreenRoute, ProjectsScreen } from "@/screens/ProjectsScreen";
import { RadarScreen } from "@/screens/RadarScreen";
import { TaskScreenRoute } from "@/screens/TaskScreen";
import { GeneralChatScreen, ThreadsScreen } from "@/screens/ThreadsScreen";

import { BusinessView } from "@/views/BusinessView";
import { ChangesView } from "@/views/ChangesView";
import { ChatView } from "@/views/ChatView";
import { InboxView } from "@/views/InboxView";
import { KitchenView } from "@/views/KitchenView";
import { KnowledgeSourceView } from "@/views/KnowledgeSourceView";
import { KnowledgeView } from "@/views/KnowledgeView";
import { MethodDetailView } from "@/views/MethodDetailView";
import { ProfileView } from "@/views/ProfileView";
import { ProjectView } from "@/views/ProjectView";
import { StaleTasksView } from "@/views/StaleTasksView";
import { TaskHistoryView } from "@/views/TaskHistoryView";
import { ToolsView } from "@/views/ToolsView";

/** An old link to a method page: methods live under Знания. */
export function MethodRedirect() {
  const { method_id } = useParams();
  return <Navigate to={`/knowledge/${method_id}`} replace />;
}

/**
 * A link from Telegram (`/chat?message=`, «Полностью») opens the whole feed on
 * that message; `/chat` itself is the list of discussions.
 */
export function opensFeed(search: string): boolean {
  return new URLSearchParams(search).has("message");
}

export function ChatEntry() {
  const [params] = useSearchParams();
  return opensFeed(params.toString()) ? <ChatView /> : <ThreadsScreen />;
}

/**
 * «Решить / Обсуждения / ●Записать / Проекты / Знания». Every link the bot sends
 * still opens: /overview is «Решить», /work is «Проекты», the inbox keeps its
 * own page, /changes and /ideas stay; the pages the new screens replaced live
 * one level down (/projects/:id/about, /knowledge/methods, /chat/all); a link
 * to a page that is gone opens «Решить», never a blank one.
 */
export function appRoutes(onSignOut: () => void): RouteObject[] {
  return [
    { path: "/", element: <DecideScreen /> },
    { path: "/overview", element: <Navigate to="/" replace /> },
    { path: "/inbox", element: <InboxView /> },
    { path: "/chat", element: <ChatEntry /> },
    { path: "/chat/general", element: <GeneralChatScreen /> },
    { path: "/chat/all", element: <ChatView /> },
    { path: "/work", element: <Navigate to="/projects" replace /> },
    { path: "/knowledge", element: <KnowledgeScreen /> },
    { path: "/knowledge/methods", element: <KnowledgeView /> },
    // Spec 023: who did what and what agents spent.
    { path: "/knowledge/stats", element: <StatsScreen /> },
    // Spec 025: our solution examples and where each one was used.
    { path: "/knowledge/cases", element: <CasesScreen /> },
    { path: "/knowledge/cases/:case_id", element: <CaseScreenRoute /> },
    // Spec 048 step 2: what staff brought from the internet — knowledge, not a tab of «Отделы».
    { path: "/knowledge/radar", element: <RadarScreen /> },
    // Spec 053, step 2: tasks closed without looking their experience through.
    { path: "/knowledge/experience", element: <ExperienceScreen /> },
    // Spec 053, step 4: the weekly duty over the methods' base.
    { path: "/knowledge/duty", element: <MethodDutyScreen /> },
    // Spec 022: an idea has its own screen; other sources keep the generic one.
    { path: "/knowledge/sources/idea/:idea_id", element: <IdeaScreenRoute /> },
    { path: "/knowledge/sources/:type/:id", element: <KnowledgeSourceView /> },
    { path: "/knowledge/:method_id", element: <MethodDetailView /> },
    { path: "/methods/:method_id", element: <MethodRedirect /> },
    { path: "/changes", element: <ChangesView /> },
    { path: "/business", element: <BusinessView /> },
    // Not a tab — the inbox lists ideas; this screen keeps what a list cannot
    // hold: вложения, обсуждение и поиск дублей.
    { path: "/ideas", element: <IdeasScreen /> },
    { path: "/projects", element: <ProjectsScreen /> },
    // Spec 035 v0.4: the company of agents — «Отделы» in place of «Агенты»; a staff member's
    // page is still the agent's, and the old list keeps opening.
    { path: "/departments", element: <DepartmentsScreen /> },
    // Spec 037/038: «О компании» and «Инструменты» are tabs of «Отделы»; their old pages lead
    // there. Before /departments/:department_id, or «company» is read as a unit's id.
    { path: "/departments/company", element: <Navigate to="/departments?tab=brief" replace /> },
    { path: "/departments/tools", element: <Navigate to="/departments?tab=tools" replace /> },
    // Spec 048: the old page of «Радар» leads to its findings in «Знания».
    { path: "/departments/radar", element: <Navigate to="/knowledge/radar" replace /> },
    // Spec 049: «Аккаунты» is a tab of «Отделы» too.
    { path: "/departments/accounts", element: <Navigate to="/departments?tab=accounts" replace /> },
    { path: "/departments/:department_id", element: <DepartmentScreenRoute /> },
    // Spec 027: named orchestrators, a section of its own.
    { path: "/agents", element: <AgentsScreen /> },
    { path: "/agents/:agent_id", element: <AgentScreenRoute /> },
    { path: "/projects/:project_id", element: <ProjectScreenRoute /> },
    { path: "/projects/:project_id/about", element: <ProjectView /> },
    // Spec 052, step 6: a client's order and a client; their lists are tabs of «Проекты».
    { path: "/orders", element: <Navigate to="/projects?tab=orders" replace /> },
    { path: "/orders/:order_id", element: <OrderScreenRoute /> },
    { path: "/clients", element: <Navigate to="/projects?tab=clients" replace /> },
    { path: "/clients/:client_id", element: <ClientScreenRoute /> },
    // FIX-027: before /tasks/:task_id, or "stale" is read as an id.
    { path: "/tasks/stale", element: <StaleTasksView /> },
    { path: "/tasks/:task_id", element: <TaskScreenRoute /> },
    { path: "/tasks/:task_id/history", element: <TaskHistoryView /> },
    { path: "/me", element: <ProfileView onSignOut={onSignOut} /> },
    { path: "/settings/agents", element: <ConnectionsScreen /> },
    { path: "/settings/agents/:connection_id", element: <ConnectionScreen /> },
    { path: "/settings/notifications", element: <NotificationsScreen /> },
    { path: "/tools", element: <ToolsView /> },
    // Living documentation of the design system; reachable by direct URL only.
    { path: "/kitchen", element: <KitchenView /> },
    { path: "*", element: <Navigate to="/" replace /> },
  ];
}
